import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input
} from 'reactstrap';
import { toast } from 'react-toastify';
import { FieldsContainer, FieldItem, SelectAllContainer } from './styles';

export const PrintFieldsSelectionModal = ({
  isOpen,
  toggle,
  onConfirm,
  defaultFields,
  customFields
}) => {
  const [selectedFields, setSelectedFields] = useState({});

  useEffect(() => {
    if (isOpen) {
      const initialSelection = {};

      if (defaultFields) {
        Object.keys(defaultFields).forEach((fieldKey) => {
          if (defaultFields[fieldKey].isActive && fieldKey !== 'files') {
            initialSelection[`default_${fieldKey}`] = true;
          }
        });
      }

      if (customFields && customFields.length > 0) {
        customFields.forEach((field) => {
          initialSelection[`custom_${field.name}`] = true;
        });
      }

      initialSelection['comments'] = true;

      setSelectedFields(initialSelection);
    }
  }, [isOpen, defaultFields, customFields]);

  const handleToggleField = (fieldKey) => {
    setSelectedFields((prev) => ({
      ...prev,
      [fieldKey]: !prev[fieldKey]
    }));
  };

  const handleSelectAll = () => {
    const allSelected = {};
    Object.keys(selectedFields).forEach((key) => {
      allSelected[key] = true;
    });
    setSelectedFields(allSelected);
  };

  const handleDeselectAll = () => {
    const allDeselected = {};
    Object.keys(selectedFields).forEach((key) => {
      allDeselected[key] = false;
    });
    setSelectedFields(allDeselected);
  };

  const handleConfirm = () => {
    const selected = Object.keys(selectedFields).filter(
      (key) => selectedFields[key]
    );

    if (selected.length === 0) {
      toast.warning('Selecione pelo menos um campo para imprimir.');
      return;
    }

    onConfirm(selectedFields);
    toggle();
  };

  const defaultFieldsNames = {
    client_id: 'Contato',
    current_responsible: 'Responsável',
    current_status: 'Status/Fase',
    description: 'Descrição',
    reason_id: 'Motivo',
    service_channel: 'Canal de atendimento',
    subreason_id: 'Submotivo',
    telephone: 'Telefone',
    title: 'Título',
    email: 'Email',
    account_id: 'Conta'
  };

  const standardFieldsToShow = useMemo(() => {
    if (!defaultFields) return [];
    return Object.keys(defaultFields).filter(
      (fieldKey) =>
        defaultFields[fieldKey].isActive &&
        fieldKey !== 'files' &&
        fieldKey !== 'description'
    );
  }, [defaultFields]);

  const totalSelected = Object.values(selectedFields).filter(Boolean).length;
  const totalFields = Object.keys(selectedFields).length;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        Selecionar Campos para Impressão
      </ModalHeader>
      <ModalBody>
        <p className="mb-3">
          Selecione os campos que deseja incluir na impressão. Informações
          sigilosas podem ser ocultadas desmarcando os campos correspondentes.
        </p>
        <SelectAllContainer>
          <Button color="primary" size="sm" onClick={handleSelectAll}>
            Selecionar Todos
          </Button>
          <Button
            color="secondary"
            size="sm"
            onClick={handleDeselectAll}
            className="ml-2"
          >
            Desmarcar Todos
          </Button>
          <span className="ml-3">
            <strong>
              {totalSelected} de {totalFields} campos selecionados
            </strong>
          </span>
        </SelectAllContainer>

        <FieldsContainer>
          {/* Campos Padrão */}
          {standardFieldsToShow.length > 0 && (
            <div className="mb-4">
              <h5 className="mb-3">Campos Padrão</h5>
              {standardFieldsToShow.map((fieldKey) => (
                <FieldItem key={fieldKey}>
                  <FormGroup check>
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={selectedFields[`default_${fieldKey}`] || false}
                        onChange={() =>
                          handleToggleField(`default_${fieldKey}`)
                        }
                      />
                      {defaultFieldsNames[fieldKey] || fieldKey}
                    </Label>
                  </FormGroup>
                </FieldItem>
              ))}
            </div>
          )}

          {/* Campo de Descrição */}
          {defaultFields &&
            defaultFields.description &&
            defaultFields.description.isActive && (
              <div className="mb-4">
                <h5 className="mb-3">Descrição</h5>
                <FieldItem>
                  <FormGroup check>
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={selectedFields['default_description'] || false}
                        onChange={() =>
                          handleToggleField('default_description')
                        }
                      />
                      Descrição do Protocolo
                    </Label>
                  </FormGroup>
                </FieldItem>
              </div>
            )}

          {/* Campos Customizados */}
          {customFields && customFields.length > 0 && (
            <div className="mb-4">
              <h5 className="mb-3">Campos Customizados</h5>
              {customFields.map((field) => (
                <FieldItem key={field.id}>
                  <FormGroup check>
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={
                          selectedFields[`custom_${field.name}`] || false
                        }
                        onChange={() =>
                          handleToggleField(`custom_${field.name}`)
                        }
                      />
                      {field.label || field.name}
                    </Label>
                  </FormGroup>
                </FieldItem>
              ))}
            </div>
          )}

          {/* Comentários */}
          <div className="mb-4">
            <h5 className="mb-3">Outros</h5>
            <FieldItem>
              <FormGroup check>
                <Label check>
                  <Input
                    type="checkbox"
                    checked={selectedFields['comments'] || false}
                    onChange={() => handleToggleField('comments')}
                  />
                  Comentários
                </Label>
              </FormGroup>
            </FieldItem>
          </div>
        </FieldsContainer>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Cancelar
        </Button>
        <Button color="primary" onClick={handleConfirm}>
          Imprimir
        </Button>
      </ModalFooter>
    </Modal>
  );
};
